import { useToasts } from "@certn/cascadia";
import { Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { array, object, string } from "yup";

import { RequestDetailsModal } from "./RequestDetailsModal";
import { RequestTypeModal } from "./RequestTypeModal";
import { useFormSubmit } from "../../../../hooks/api/useSubmit";
import { ActionListItemWithModal } from "../ActionListItemWithModal";

export function RequestPersonalData() {
  const { t } = useTranslation();
  const { show } = useToasts();
  const title = t("Personal data request");
  const [showDetails, setShowDetails] = useState<boolean>();

  const submitForm = useFormSubmit("/internal/people/me/request-data/", {
    onSuccess: () =>
      show({
        title: t("Request sent"),
        type: "info",
      }),
    raiseError: true,
  });

  const schema = object().shape({
    request_types: array()
      .min(1, t("Please select at least one option."))
      .of(string()),

    request_details: string().required().label(t("Request details")),
  });

  return (
    <ActionListItemWithModal
      testID="help-and-privacy-personal-data-request-button"
      title={title}
      description={t(
        "Request, delete, or make access changes to your personal data"
      )}
      link={t("Request")}
    >
      {(close: () => void) => {
        const handleClose = () => {
          setShowDetails(false);
          close();
        };

        return (
          <Formik
            initialValues={{
              request_types: [],
              request_details: "",
              supporting_documents: [],
            }}
            validationSchema={schema}
            onSubmit={async (values, helpers) => {
              try {
                const supportingDocuments = values.supporting_documents.map(
                  ({ content }) => content
                );
                await submitForm(
                  { ...values, supporting_documents: supportingDocuments },
                  helpers
                );
                handleClose();
              } catch (e) {
                console.error('Error in "Personal data request":', e);
                show({
                  title: t("Something went wrong, try again"),
                  type: "error",
                });
              }
            }}
          >
            {({ handleSubmit, values, setErrors }) =>
              showDetails ? (
                <RequestDetailsModal
                  onSubmit={handleSubmit}
                  onBack={() => setShowDetails(false)}
                  onClose={handleClose}
                />
              ) : (
                <RequestTypeModal
                  onNext={() => {
                    if (values.request_types.length === 0) {
                      setErrors({
                        request_types: t("Please select at least one option."),
                      });
                      return;
                    }
                    setShowDetails(true);
                  }}
                  onClose={handleClose}
                />
              )
            }
          </Formik>
        );
      }}
    </ActionListItemWithModal>
  );
}
